/**
 * City detection and geolocation distance utilities.
 * Used to detect whether a user's message references a city
 * more than 300 miles from their current location.
 */

export interface KnownCity {
  name: string;
  lat: number;
  lng: number;
}

/** Major world cities and travel destinations with approximate coordinates */
export const KNOWN_CITIES: KnownCity[] = [
  // North America
  { name: 'New York', lat: 40.7128, lng: -74.006 },
  { name: 'Los Angeles', lat: 34.0522, lng: -118.2437 },
  { name: 'Chicago', lat: 41.8781, lng: -87.6298 },
  { name: 'Miami', lat: 25.7617, lng: -80.1918 },
  { name: 'San Francisco', lat: 37.7749, lng: -122.4194 },
  { name: 'Las Vegas', lat: 36.1699, lng: -115.1398 },
  { name: 'Seattle', lat: 47.6062, lng: -122.3321 },
  { name: 'Boston', lat: 42.3601, lng: -71.0589 },
  { name: 'Denver', lat: 39.7392, lng: -104.9903 },
  { name: 'Orlando', lat: 28.5383, lng: -81.3792 },
  { name: 'Houston', lat: 29.7604, lng: -95.3698 },
  { name: 'Phoenix', lat: 33.4484, lng: -112.074 },
  { name: 'Dallas', lat: 32.7767, lng: -96.797 },
  { name: 'Atlanta', lat: 33.749, lng: -84.388 },
  { name: 'Aspen', lat: 39.1911, lng: -106.8175 },
  { name: 'Cancún', lat: 21.1619, lng: -86.8515 },
  { name: 'Cancun', lat: 21.1619, lng: -86.8515 },
  { name: 'Tulum', lat: 20.2107, lng: -87.4654 },
  { name: 'Mexico City', lat: 19.4326, lng: -99.1332 },
  { name: 'Havana', lat: 23.1136, lng: -82.3666 },
  { name: 'Costa Rica', lat: 9.9281, lng: -84.0907 },
  // Canada
  { name: 'Toronto', lat: 43.6532, lng: -79.3832 },
  { name: 'Montreal', lat: 45.5017, lng: -73.5673 },
  { name: 'Vancouver', lat: 49.2827, lng: -123.1207 },
  // South America
  { name: 'Buenos Aires', lat: -34.6037, lng: -58.3816 },
  { name: 'Rio de Janeiro', lat: -22.9068, lng: -43.1729 },
  { name: 'São Paulo', lat: -23.5505, lng: -46.6333 },
  { name: 'Sao Paulo', lat: -23.5505, lng: -46.6333 },
  { name: 'Lima', lat: -12.0464, lng: -77.0428 },
  { name: 'Bogotá', lat: 4.711, lng: -74.0721 },
  { name: 'Bogota', lat: 4.711, lng: -74.0721 },
  { name: 'Santiago', lat: -33.4489, lng: -70.6693 },
  { name: 'Patagonia', lat: -51.623, lng: -69.2168 },
  // Europe
  { name: 'London', lat: 51.5074, lng: -0.1278 },
  { name: 'Paris', lat: 48.8566, lng: 2.3522 },
  { name: 'Rome', lat: 41.9028, lng: 12.4964 },
  { name: 'Barcelona', lat: 41.3851, lng: 2.1734 },
  { name: 'Madrid', lat: 40.4168, lng: -3.7038 },
  { name: 'Amsterdam', lat: 52.3676, lng: 4.9041 },
  { name: 'Berlin', lat: 52.52, lng: 13.405 },
  { name: 'Vienna', lat: 48.2082, lng: 16.3738 },
  { name: 'Prague', lat: 50.0755, lng: 14.4378 },
  { name: 'Budapest', lat: 47.4979, lng: 19.0402 },
  { name: 'Athens', lat: 37.9838, lng: 23.7275 },
  { name: 'Istanbul', lat: 41.0082, lng: 28.9784 },
  { name: 'Lisbon', lat: 38.7223, lng: -9.1393 },
  { name: 'Copenhagen', lat: 55.6761, lng: 12.5683 },
  { name: 'Munich', lat: 48.1351, lng: 11.582 },
  { name: 'Milan', lat: 45.4642, lng: 9.19 },
  { name: 'Florence', lat: 43.7696, lng: 11.2558 },
  { name: 'Venice', lat: 45.4408, lng: 12.3155 },
  { name: 'Naples', lat: 40.8518, lng: 14.2681 },
  { name: 'Bologna', lat: 44.4949, lng: 11.3426 },
  { name: 'Lyon', lat: 45.764, lng: 4.8357 },
  { name: 'Provence', lat: 43.9493, lng: 4.8055 },
  { name: 'Santorini', lat: 36.3932, lng: 25.4615 },
  { name: 'Mykonos', lat: 37.4467, lng: 25.3289 },
  { name: 'Amalfi', lat: 40.6344, lng: 14.6024 },
  { name: 'Monaco', lat: 43.7384, lng: 7.4246 },
  { name: 'Zurich', lat: 47.3769, lng: 8.5417 },
  { name: 'Marrakech', lat: 31.6295, lng: -7.9811 },
  { name: 'Tbilisi', lat: 41.6938, lng: 44.8015 },
  { name: 'Bucharest', lat: 44.4268, lng: 26.1025 },
  { name: 'Sofia', lat: 42.6977, lng: 23.3219 },
  { name: 'Kraków', lat: 50.0647, lng: 19.945 },
  { name: 'Krakow', lat: 50.0647, lng: 19.945 },
  { name: 'Moscow', lat: 55.7558, lng: 37.6173 },
  { name: 'Iceland', lat: 64.1355, lng: -21.8954 },
  { name: 'Reykjavik', lat: 64.1355, lng: -21.8954 },
  { name: 'Norwegian Fjords', lat: 60.3913, lng: 5.3221 },
  { name: 'Bergen', lat: 60.3913, lng: 5.3221 },
  // Middle East
  { name: 'Dubai', lat: 25.2048, lng: 55.2708 },
  // Africa
  { name: 'Cape Town', lat: -33.9249, lng: 18.4241 },
  { name: 'Cairo', lat: 30.0444, lng: 31.2357 },
  { name: 'Nairobi', lat: -1.2921, lng: 36.8219 },
  { name: 'Zanzibar', lat: -6.1659, lng: 39.2026 },
  // Asia
  { name: 'Tokyo', lat: 35.6762, lng: 139.6503 },
  { name: 'Kyoto', lat: 35.0116, lng: 135.7681 },
  { name: 'Seoul', lat: 37.5665, lng: 126.978 },
  { name: 'Bangkok', lat: 13.7563, lng: 100.5018 },
  { name: 'Singapore', lat: 1.3521, lng: 103.8198 },
  { name: 'Dubai', lat: 25.2048, lng: 55.2708 },
  { name: 'Mumbai', lat: 19.076, lng: 72.8777 },
  { name: 'Delhi', lat: 28.6139, lng: 77.209 },
  { name: 'Beijing', lat: 39.9042, lng: 116.4074 },
  { name: 'Shanghai', lat: 31.2304, lng: 121.4737 },
  { name: 'Bali', lat: -8.4095, lng: 115.1889 },
  { name: 'Phuket', lat: 7.9519, lng: 98.3381 },
  { name: 'Maldives', lat: 4.1755, lng: 73.5093 },
  { name: 'Nepal', lat: 27.7172, lng: 85.324 },
  { name: 'Kathmandu', lat: 27.7172, lng: 85.324 },
  { name: 'Borneo', lat: 5.9788, lng: 116.0735 },
  // Oceania
  { name: 'Sydney', lat: -33.8688, lng: 151.2093 },
  { name: 'Melbourne', lat: -37.8136, lng: 144.9631 },
  { name: 'New Zealand', lat: -36.8509, lng: 174.7645 },
  { name: 'Auckland', lat: -36.8509, lng: 174.7645 },
  { name: 'Bora Bora', lat: -16.5004, lng: -151.7415 },
  // Caribbean
  { name: 'St. Barts', lat: 17.9, lng: -62.8333 },
  { name: 'Hawaii', lat: 21.3069, lng: -157.8583 },
];

/** Haversine formula: great-circle distance in miles between two lat/lng points */
export function haversineDistanceMiles(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3958.8; // Earth radius in miles
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Get the user's current coordinates via the Geolocation API */
export function getUserLocation(): Promise<{ lat: number; lng: number } | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { timeout: 5000 }
    );
  });
}

/**
 * Scan `message` for any known city name. If found and the city is more than
 * `thresholdMiles` from `userLat/userLng`, return that city; otherwise null.
 */
export function findFarCityInMessage(
  message: string,
  userLat: number,
  userLng: number,
  thresholdMiles = 300
): KnownCity | null {
  const lower = message.toLowerCase();
  for (const city of KNOWN_CITIES) {
    if (lower.includes(city.name.toLowerCase())) {
      const dist = haversineDistanceMiles(userLat, userLng, city.lat, city.lng);
      if (dist > thresholdMiles) {
        return city;
      }
    }
  }
  return null;
}

/**
 * Full async check: get user location, scan message for a far city.
 * Resolves to the matched city or null.
 */
export async function detectFarCityInMessage(message: string, thresholdMiles = 300): Promise<KnownCity | null> {
  const userLocation = await getUserLocation();
  if (!userLocation) return null;
  return findFarCityInMessage(message, userLocation.lat, userLocation.lng, thresholdMiles);
}

